import { memo, useMemo } from 'react';
import { Button as AntdButton } from 'antd';
import type { ButtonProps, ButtonVariant } from './types';
import './Button.css';

/**
 * Maps each visual variant to the underlying Ant Design `type`.
 *
 * The filled variants (`primary` / `secondary` / `ghost`) render on top of the
 * AntD `default` button and are painted entirely through `Button.css` with the
 * `--idf-*` design tokens. The `text` and `link` variants reuse AntD's `text`
 * type to drop the button chrome and behave like inline links — `link` being a
 * smaller, bolder, underline-on-hover flavor of `text`.
 */
const VARIANT_TO_ANTD_TYPE: Record<ButtonVariant, 'default' | 'text'> = {
  primary: 'default',
  secondary: 'default',
  ghost: 'default',
  text: 'text',
  link: 'text',
};

/**
 * Button Atom
 *
 * Reusable button wrapping Ant Design's `Button`, styled with the Experian IDF
 * design tokens (`--idf-*`). Centralises the variant/size/hover/disabled states
 * that were previously duplicated (with `!important` specificity hacks) across
 * the auth forms.
 *
 * The button stretches to fill its container by default (`fullWidth`); pass
 * `fullWidth={false}` for an inline-sized button, or `maxWidth` to cap it. Other
 * Ant Design `Button` props pass through, so `icon` (always rendered to the left
 * of the label), `loading`, `htmlType`, `disabled` and `onClick` work as usual.
 * User-facing text is provided by the caller via `children` (translate with
 * `t()` at the call site).
 *
 * @example
 * ```tsx
 * // Primary call to action: full width by default, capped at 326px, 40px tall
 * <Button variant="primary" size="lg" maxWidth={326} htmlType="submit">
 *   {t('Login')}
 * </Button>
 *
 * // Tertiary "back" link with a left icon, inline width
 * <Button
 *   variant="text"
 *   fullWidth={false}
 *   icon={<ArrowLeftOutlined />}
 *   onClick={goBack}
 * >
 *   {t('Back to Login')}
 * </Button>
 * ```
 *
 * @param props - The component props
 * @returns A memoized, token-styled button
 */
const Button = memo(
  ({
    variant = 'primary',
    size = 'md',
    fullWidth = true,
    maxWidth,
    className = '',
    style,
    children,
    ...rest
  }: ButtonProps) => {
    const cssClasses = useMemo(
      () =>
        [
          'idf-button',
          `idf-button--${variant}`,
          `idf-button--${size}`,
          // Left icon sits closer to the edge: tighten the left padding.
          rest.icon ? 'idf-button--with-icon' : '',
          className,
        ]
          .filter(Boolean)
          .join(' '),
      [variant, size, rest.icon, className]
    );

    const mergedStyle = useMemo(() => {
      if (maxWidth == null) return style;
      const cap = typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth;
      // Caller-provided style wins, so an explicit `style.maxWidth` overrides.
      return { maxWidth: cap, ...style };
    }, [maxWidth, style]);

    return (
      <AntdButton
        type={VARIANT_TO_ANTD_TYPE[variant]}
        block={fullWidth}
        className={cssClasses}
        style={mergedStyle}
        {...rest}
      >
        {children}
      </AntdButton>
    );
  }
);

// Set display name for better debugging experience
Button.displayName = 'Button';

export default Button;
